import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";


export const desktopRoot =
  (isRestricted: boolean): SxProps<Theme> =>
  (theme) => ({
    display: { xs: "none", md: "flex" },
    alignItems: "center",
    gap: 2,
    px: 2.5,
    py: 2.25,
    borderRadius: 2.25,
    border: 1,
    borderColor: isRestricted ? alpha(theme.palette.error.main, 0.3) : "divider",
    bgcolor: isRestricted
      ? alpha(theme.palette.error.main, 0.03)
      : "background.paper",
  });

export const mobileRoot =
  (isRestricted: boolean): SxProps<Theme> =>
  (theme) => ({
    display: { xs: "flex", md: "none" },
    flexDirection: "column",
    gap: 1.25,
    p: 1.75,
    borderRadius: 2,
    border: 1,
    borderColor: isRestricted ? alpha(theme.palette.error.main, 0.3) : "divider",
    bgcolor: isRestricted
      ? alpha(theme.palette.error.main, 0.03)
      : "background.paper",
  });

export const avatar =
  (isRestricted: boolean): SxProps<Theme> =>
  (theme) => ({
    width: { xs: 38, md: 46 },
    height: { xs: 38, md: 46 },
    borderRadius: { xs: 1.375, md: 1.625 },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontWeight: 700,
    fontSize: { xs: "0.875rem", md: "1.0625rem" },
    bgcolor: isRestricted
      ? alpha(theme.palette.error.main, 0.1)
      : alpha(theme.palette.primary.main, 0.08),
    color: isRestricted ? theme.palette.error.main : theme.palette.primary.main,
  });

export const desktopInfo: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 0.375,
};

export const desktopNameRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
};

export const desktopName: SxProps<Theme> = {
  fontSize: "0.90625rem",
  fontWeight: 700,
};

export const subText =
  (isError: boolean): SxProps<Theme> =>
  (theme) => ({
    fontSize: "0.75rem",
    color: isError ? theme.palette.error.main : theme.palette.text.secondary,
  });

export const carryOverNote: SxProps<Theme> = (theme) => ({
  fontSize: "0.6875rem",
  fontWeight: 700,
  color: theme.palette.primary.main,
});

export const progressTrack: SxProps<Theme> = (theme) => ({
  height: 5,
  borderRadius: 0.5,
  bgcolor: theme.palette.background.default,
  overflow: "hidden",
  maxWidth: 240,
  mt: 0.5,
});

export const progressFill =
  (percent: number, isError: boolean): SxProps<Theme> =>
  (theme) => ({
    height: "100%",
    borderRadius: 0.5,
    width: `${percent}%`,
    bgcolor: isError ? theme.palette.error.main : theme.palette.primary.main,
  });

export const desktopPriceBlock: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: 0.25,
  flexShrink: 0,
};

export const priceValue: SxProps<Theme> = {
  fontSize: "0.875rem",
  fontWeight: 800,
};

export const priceUnit: SxProps<Theme> = {
  fontSize: "0.65625rem",
  fontWeight: 500,
  color: "text.secondary",
};

export const payButtonDesktop: SxProps<Theme> = {
  minWidth: 140,
  height: 42,
  fontWeight: 700,
  borderRadius: 1.5,
  flexShrink: 0,
};

export const mobileHeader: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
};

export const mobileNameBlock: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
};

export const mobileName: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 700,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const mobileProgressTrack: SxProps<Theme> = (theme) => ({
  height: 5,
  borderRadius: 0.5,
  bgcolor: theme.palette.background.default,
  overflow: "hidden",
});

export const mobileFooter: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderTop: `1px dashed ${theme.palette.divider}`,
  pt: 1.25,
});

export const mobilePriceValue: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 700,
};

export const payButtonMobile: SxProps<Theme> = {
  minWidth: 120,
  height: 38,
  fontWeight: 700,
  borderRadius: 1.5,
  fontSize: "0.8125rem",
};
